import { Progress as BaseProgress } from "@base-ui/react/progress";
import { CircleCheck, CircleX } from "lucide-react";
import type { FunctionComponent, ReactNode } from "react";
import { cn } from "./cn";

export type ProgressStatus = "normal" | "active" | "success" | "exception";

const STATUS_CLASSES: Record<ProgressStatus, string> = {
  normal: "bg-noa-primary",
  active: "bg-noa-primary animate-pulse",
  success: "bg-noa-success",
  exception: "bg-noa-danger",
};

export interface ProgressProps {
  /** 0–100. */
  percent?: number;
  /** Default `success` at 100, `normal` otherwise (as antd). */
  status?: ProgressStatus;
  size?: "default" | "small";
  /** Label on the right (default true). */
  showInfo?: boolean;
  /** Custom label; overrides the success/exception icons, like antd. */
  format?: (percent: number) => ReactNode;
  /** Accessible name of the bar. */
  "aria-label"?: string;
  /** Screen-reader text for the current value; overrides the default percentage. */
  "aria-valuetext"?: string;
  className?: string;
}

/** Linear progress bar (antd `Progress` type="line" replacement) on Base UI Progress. */
export const Progress: FunctionComponent<ProgressProps> = ({
  percent = 0,
  status,
  size = "default",
  showInfo = true,
  format,
  "aria-label": ariaLabel,
  "aria-valuetext": ariaValueText,
  className,
}) => {
  const value = Math.min(100, Math.max(0, percent));
  const resolvedStatus = status ?? (value >= 100 ? "success" : "normal");

  const renderInfo = () => {
    if (format) return format(value);
    if (resolvedStatus === "success") return <CircleCheck className="size-3.5 text-noa-success" />;
    if (resolvedStatus === "exception") return <CircleX className="size-3.5 text-noa-danger" />;
    return `${Math.round(value)}%`;
  };

  return (
    <BaseProgress.Root
      value={value}
      {...(ariaLabel !== undefined && { "aria-label": ariaLabel })}
      {...(ariaValueText !== undefined && { "aria-valuetext": ariaValueText })}
      className={cn("flex w-full items-center gap-2 text-sm text-noa", className)}
    >
      <BaseProgress.Track
        className={cn("flex-1 overflow-hidden rounded-full bg-noa-hover", size === "small" ? "h-1.5" : "h-2")}
      >
        <BaseProgress.Indicator
          className={cn("h-full rounded-full transition-[width] duration-300", STATUS_CLASSES[resolvedStatus])}
        />
      </BaseProgress.Track>
      {showInfo && (
        <span
          className={cn(
            "inline-flex min-w-[2em] shrink-0 items-center whitespace-nowrap",
            size === "small" && "text-xs"
          )}
        >
          {renderInfo()}
        </span>
      )}
    </BaseProgress.Root>
  );
};
